import * as methods from 'api/core/actions';
import { partition } from 'lodash-es';
import { setLegacyTimestampField } from 'redux/modules/hours.reducer';
import type { BulkUpdateResponse } from 'redux/modules/superreports.types';
import type { Hour, NormalizedHourToApi, SetTimerState } from 'types/hour';
import type { LightProject, Project } from 'types/project';
import type { Suggestion } from 'types/suggestion';
import type { User } from 'types/user';
import { encodeUrlParams } from 'utils/url-params';

interface SaveHourOptions {
  setTimerState?: SetTimerState;
}

export async function getHours({
  since,
  upto,
  userId,
}: {
  upto: string;
  since: string;
  userId: number;
}): Promise<Array<Hour<LightProject>>> {
  const params = encodeUrlParams({ since, upto, user_id: userId, projects_version: 3 });
  return methods.Get(`hours.json${params}`);
}

export async function getHourIds(hour_ids: Array<number>): Promise<Array<Hour<LightProject>>> {
  const params = encodeUrlParams({ hour_ids, projects_version: 3 });
  return methods.Get(`hours.json${params}`);
}

export async function saveHour(
  hour: NormalizedHourToApi,
  options: SaveHourOptions = {}
): Promise<Hour<Project>> {
  // Synthetic memories have negative IDs, so don't make API requests for these
  const [realEntryIds, syntheticEntryIds] = partition(hour.entry_ids, id => id >= 0);

  const payload = {
    ...hour,
    ...setLegacyTimestampField(hour),
    entry_ids: realEntryIds,
  };
  const correctedPayload = {
    ...payload,
    state_id: payload.state_id === 0 ? null : payload.state_id,
  };
  const shouldStartTimer = options.setTimerState === 'start';
  const shouldStopTimer = options.setTimerState === 'stop';
  let response: Promise<Hour<Project>>;
  if (payload.id) {
    // Timer are on different endpoint _for now_; This is going away, so I'm
    // keeping this "hidden logic" here, as it'll be removed... I think.
    if (shouldStartTimer) {
      response = methods.Put(`hours/${payload.id}/start`, { event: { id: payload.id } });
    } else if (shouldStopTimer) {
      response = methods.Put(`hours/${payload.id}/stop`, { event: { id: payload.id } });
    } else {
      response = methods.Put(`hours/${payload.id}`, { event: correctedPayload });
    }
  } else if (shouldStartTimer) {
    throw new Error('Can not start a timer in an hour that has not an id.');
  } else {
    response = methods.Post('hours', {
      event: correctedPayload,
    });
  }

  const updatedHour = await response;

  // Restore synthetic memories after saving to keep FE state consistent
  if (syntheticEntryIds.length) {
    updatedHour.entry_ids = updatedHour.entry_ids.concat(syntheticEntryIds);
  }

  return updatedHour;
}

export async function deleteHour(id: number): Promise<unknown> {
  return methods.Delete(`hours/${id}`);
}

export async function getSuggestions(
  since: string | null | undefined = null,
  upto: string | null | undefined = null
) {
  if (!since || !upto) {
    return Promise.reject();
  }
  return methods.Get(`suggested_hours?since=${since}&until=${upto}`);
}

export async function getSuggestionIds(
  suggested_hour_ids: Array<number>
): Promise<Array<Suggestion<User>>> {
  const params = encodeUrlParams({ suggested_hour_ids });
  return methods.Get(`suggested_hours${params}`);
}

export async function dismissSuggestionId(id: number) {
  if (id < 0) return;

  return methods.Put(`suggested_hours/${id}`);
}

export async function updateSuggestionIdEntries(
  id: number,
  entryIds: Array<number>
): Promise<Suggestion<User>> {
  // Synthetic memories have negative IDs, so don't make API requests for these
  const [realEntryIds, syntheticEntryIds] = partition(entryIds, id => id >= 0);

  const updatedSuggestion: Suggestion<User> = await methods.Put(`suggested_hours/${id}`, {
    suggested_hour: { entry_ids: realEntryIds },
  });

  // Restore synthetic memories after saving to keep FE state consistent
  if (syntheticEntryIds.length) {
    updatedSuggestion.suggested_entry_ids =
      updatedSuggestion.suggested_entry_ids.concat(syntheticEntryIds);
  }

  return updatedSuggestion;
}

export async function bulkUpdateHours(
  payload: Array<NormalizedHourToApi>
): Promise<BulkUpdateResponse> {
  const correctedPayload = payload.map(hour => ({
    ...hour,
    state_id: hour.state_id === 0 ? null : hour.state_id,
  }));
  return methods.Post('bulk/hours', { update: correctedPayload });
}

export async function bulkDeleteHours(
  payload: Array<Hour<number> | number>
): Promise<BulkUpdateResponse> {
  if (payload.length <= BULK_CHUNK_SIZE) {
    return methods.Post('bulk/hours', { delete: payload });
  }
  // Chunk to stay inside the BE's sync threshold. Chunk-level transport errors
  // bubble; a successful response with fewer deleted_ids than requested (BE
  // saying some rows were already gone) flows through and is the caller's call
  // to tolerate or surface.
  const aggregated = emptyBulkResponse();
  for (const chunk of chunkArray(payload, BULK_CHUNK_SIZE)) {
    const response: BulkUpdateResponse = await methods.Post('bulk/hours', { delete: chunk });
    aggregated.deleted_ids.push(...response.deleted_ids);
  }
  return aggregated;
}

/**
 * Server-side revert: deletes every hour stamped with this AutoSheet proposal id
 * in a single round-trip (no ID list, no chunking). Returns BulkUpdateResponse;
 * `deleted_ids` is empty for pre-stamping proposals whose rows have a NULL column.
 */
export async function bulkDeleteHoursByAutosheetProposalId(
  proposalId: string
): Promise<BulkUpdateResponse> {
  return methods.Post('bulk/hours', { delete_by_autosheet_proposal_id: proposalId });
}

export async function bulkCopyHours(
  payload: Array<NormalizedHourToApi>
): Promise<BulkUpdateResponse> {
  const correctedPayload = payload.map(hour => ({
    ...hour,
    state_id: hour.state_id === 0 ? null : hour.state_id,
  }));
  return methods.Post('bulk/hours', { create: correctedPayload });
}

/**
 * Saves or updates multiple hours, chunking to stay inside the BE's sync mode
 * threshold. Above the threshold the BE switches to async (job-id) mode whose
 * completion only surfaces via Pusher; chunking sidesteps that path entirely.
 *
 * On a chunk error response, returns aggregated results so the caller's
 * existing rollback path (`bulkDeleteHours(response.created_ids)`) cleans up
 * earlier chunks. On a chunk throw, rolls back internally before re-throwing.
 */
export async function bulkSaveHours(
  payload: Array<NormalizedHourToApi>
): Promise<BulkUpdateResponse> {
  const correctedPayload = payload.map(hour => ({
    ...hour,
    state_id: hour.state_id === 0 ? null : hour.state_id,
  }));

  if (correctedPayload.length <= BULK_CHUNK_SIZE) {
    const [update, create] = partition(correctedPayload, hour => !!hour.id);
    return methods.Post('bulk/hours', { create, update });
  }

  const aggregated = emptyBulkResponse();
  for (const chunk of chunkArray(correctedPayload, BULK_CHUNK_SIZE)) {
    let response: BulkUpdateResponse;
    try {
      const [update, create] = partition(chunk, hour => !!hour.id);
      response = await methods.Post('bulk/hours', { create, update });
    } catch (err) {
      if (aggregated.created_ids.length > 0) {
        try {
          await bulkDeleteHours(aggregated.created_ids);
        } catch {
          // Rollback itself failed. Swallow so the original chunk error stays
          // the surface error — orphans are unfortunate but expected here.
        }
      }
      throw err;
    }

    aggregated.created_ids.push(...response.created_ids);
    aggregated.updated_ids.push(...response.updated_ids);

    if (response.errors.create.length > 0 || response.errors.update.length > 0) {
      aggregated.errors.create.push(...response.errors.create);
      aggregated.errors.update.push(...response.errors.update);
      return aggregated;
    }
  }
  return aggregated;
}

// Bulk-hours BE switches from sync to async (job-id) mode at 20 rows. 19 is
// the largest chunk that's guaranteed to come back sync.
const BULK_CHUNK_SIZE = 19;

function chunkArray<T>(arr: ReadonlyArray<T>, size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const emptyBulkResponse = (): BulkUpdateResponse => ({
  created_ids: [],
  deleted_ids: [],
  updated_ids: [],
  errors: { create: [], update: [] },
});
